import { DialogFooter, Dialog, DialogContent } from '@readcloud/ui/dialog';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@readcloud/ui/form';
import { SelectContentType } from './SelectContentType';
import * as React from 'react';
import {
  FileText,
  Image,
  Upload,
  Check,
  Cloud,
  ListCollapse,
} from 'lucide-react';
import { Step, Stepper } from '../Stepper/Stepper';
import { UploadWizardFooter } from './UploadWizardFooter';
import { UploadWizardHeader } from './UploadWizardHeader';
import { UploadStepContent } from './StepContent';
import UploadContentForm from './UploadContentForm';
import UploadThumbnail from './UploadThumbnail';
import AddContentDetails from './AddContentDetails';
import SelectClouds from './SelectClouds';
import TermsAndConditions from './TermsAndConditions';
import {
  contentFieldSchema,
  thumbnailFieldSchema,
  contentDetailsFieldSchema,
  cloudsFieldSchema,
  termsFieldSchema,
} from './schemas/formFields';
import { useReadCloudAuth } from '@readcloud/common';
import { useUploadMutations } from './hooks/useUploadMutations';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@readcloud/ui/alert-dialog';
import { Button } from '@readcloud/ui/button';
import { MimeTypes } from './types/mimeTypes';
import { useCloudsAndContentActions } from '../../context/ActionsContext';

interface IUploadWizardDialogProps extends React.ComponentProps<typeof Dialog> {
  cloudId: string;
}

export const uploadWizardSchema = z.object({
  ...contentFieldSchema.shape,
  ...thumbnailFieldSchema.shape,
  ...contentDetailsFieldSchema.shape,
  ...cloudsFieldSchema.shape,
  ...termsFieldSchema.shape,
});

export type UploadWizardFormData = z.infer<typeof uploadWizardSchema>;

export const UploadWizardDialog: React.FunctionComponent<
  IUploadWizardDialogProps
> = ({ open, onOpenChange, cloudId }) => {
  const methods = useForm<UploadWizardFormData>({
    resolver: zodResolver(uploadWizardSchema),
  });

  const [submitting, setSubmitting] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [showCloseAlert, setShowCloseAlert] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [termsChecked, setTermsChecked] = React.useState<boolean>(false);

  const { user } = useReadCloudAuth();

  const { refresh } = useCloudsAndContentActions();
  const {
    uploadThumbnailMutation,
    uploadContentMutation,
    submitContentMutation,
  } = useUploadMutations(user);

  const [fileData, setFileData] = React.useState<
    | {
        type: MimeTypes;
        name: string;
      }
    | undefined
  >();

  const formClouds = methods.watch('clouds') || [];

  React.useEffect(() => {
    if (cloudId) {
      methods.setValue('clouds', [...formClouds, cloudId]);
    }
  }, [cloudId]);

  React.useEffect(() => {
    if (fileData?.name) {
      methods.setValue(
        'title',
        fileData?.name.split('.').slice(0, -1).join('.')
      );
    }
  }, [fileData?.name]);

  const getFieldsToReset = (changedField: string) => {
    const fieldOrder = {
      'content.type': 0,
      'content.url': 1,
      'content.file': 1,
      thumbnail: 2,
      title: 3,
      description: 3,
      printable: 3,
      comments: 3,
      clouds: 4,
      termsAndConditions: 5,
    };

    const changedFieldOrder =
      fieldOrder[changedField as keyof typeof fieldOrder] ?? -1;

    return Object.entries(fieldOrder)
      .filter(([key, order]) => order > changedFieldOrder)
      .map(
        ([field]) =>
          field as
            | keyof UploadWizardFormData
            | 'content.type'
            | 'content.url'
            | 'content.file'
      );
  };

  React.useEffect(() => {
    const subscription = methods.watch((values, { name, type }) => {
      if (!name || type !== 'change') return;
      const fieldsToReset = getFieldsToReset(name);
      fieldsToReset.forEach((field) => {
        if (field === 'clouds') {
          methods.setValue(field, [cloudId]);
        } else {
          methods.setValue(field, undefined);
        }
      });
    });

    return () => subscription.unsubscribe();
  }, [methods]);

  const handleFinalSubmit = async (data: UploadWizardFormData) => {
    try {
      setSubmitting(true);
      setUploading(true);
      const contentInput = {
        file: data.content.file || undefined,
        url: data.content.url || undefined,
        type: data.content.type,
      };

      const contentResponse = await uploadContentMutation.mutateAsync(
        contentInput
      );
      const contentId = contentResponse?.userUploadId;

      const thumbnailResponse = await uploadThumbnailMutation.mutateAsync(
        data.thumbnail
      );
      const thumbnailId = thumbnailResponse?.userUploadId;

      await submitContentMutation.mutateAsync({
        ...data,
        contentId: contentId,
        thumbnailId: thumbnailId,
      });
    } catch (error) {
      setError('Failed to upload content. Please try again.');
      console.error('Error during submission:', error);
      return;
    } finally {
      setSubmitting(false);
      if (!error) {
        setTermsChecked(false);
        methods.resetField('termsAndConditions');
        refresh && refresh();
        onOpenChange && onOpenChange(false);
      }
    }
  };

  const steps = [
    {
      id: 'select-content-type',
      label: 'Content Type',
      icon: FileText,
      description: 'Select the type of content you want to upload',
      component: <SelectContentType />,
    },
    {
      id: 'upload-content',
      icon: Upload,
      label: 'Upload Content',
      description: 'Select the content you want to upload',
      component: <UploadContentForm />,
    },
    {
      id: 'select-thumbnail',
      label: 'Select Thumbnail',
      icon: Image,
      description: 'Select the thumbnail for your content',
      component: (
        <UploadThumbnail
          setFileData={setFileData}
          fileData={fileData}
          type={methods.getValues('content.type')}
          file={methods.getValues('content.file')}
          url={methods.getValues('content.url')}
        />
      ),
    },
    {
      id: 'content-details',
      label: 'Content Details',
      icon: ListCollapse,
      description: 'Enter the details for your content',
      component: (
        <AddContentDetails
          thumbnail={methods.getValues('thumbnail')}
          type={methods.getValues('content.type')}
        />
      ),
    },
    {
      id: 'select-clouds',
      label: 'Select Clouds',
      icon: Cloud,
      description: 'Select the clouds you want to upload your content to',
      component: <SelectClouds />,
    },
    {
      id: 'terms-and-conditions',
      label: 'Terms and Conditions',
      icon: Check,
      description: 'Accept the terms and conditions',
      component: <TermsAndConditions setTermsChecked={()=> {setTermsChecked(!termsChecked)}} />,
    },
  ];

  const handleClose = () => {
    const formValues = methods.getValues([
      'content.file',
      'content.url',
      'thumbnail',
      'title',
      'description',
      'printable',
      'comments',
    ]);
    const hasValues = formValues.some((value) => {
      if (value !== undefined && value !== '') {
        return true;
      }
    });

    if (hasValues && !submitting) {
      setShowCloseAlert(true);
    } else {
      onOpenChange?.(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(open) => {
          if (!open) handleClose();
          else onOpenChange?.(true);
        }}
      >
        <DialogContent className="rc-max-h-full rc-w-[95%] rc-overflow-y-auto !rc-z-[300] max-sm:rc-p-4 rc-rounded-lg max-md:rc-max-w-sm rc-border-0">
          <div className="rc-h-full">
            {submitting && (
              
              <div className='rc-absolute !rc-z-[4000] rc-overflow-hidden rc-bg-black rc-bg-opacity-50 rc-inset-0  rc-flex rc-items-center rc-justify-center rc-flex-col rc-w-full rc-h-full rc-text-lg
              '>
                <div className='rc-bg-white rc-rounded-md rc-p-4 rc-w-[95%] rc-flex rc-flex-col rc-items-center rc-justify-center rc-gap-2'>
                  <div role="status">
                    <svg
                      aria-hidden="true"
                      className="rc-w-8 rc-h-8 rc-text-gray-200 rc-animate-spin dark:rc-text-gray-600 rc-fill-primary"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  </div>
                  <span >Uploading...</span>
                </div>
              </div>
            )}
            <FormProvider {...methods}>
              <Form {...methods}>
                <Stepper
                  responsive={false}
                  initialStep={0}
                  steps={steps}
                  size="sm"
                  styles={{ 
                    'main-container': 'rc-pt-6',
                  }}
                >
                  {steps.map((stepProps) => (
                    <Step key={stepProps.id} icon={stepProps.icon} />
                  ))}
                  <div className="rc-flex rc-flex-col rc-gap-4 sm:rc-gap-6">
                    <UploadWizardHeader steps={steps} />
                    <UploadStepContent steps={steps} />
                    <DialogFooter>
                      <UploadWizardFooter
                        onSubmit={() => handleFinalSubmit(methods.getValues())}
                        isChecked={termsChecked}
                      />
                    </DialogFooter>
                  </div>
                </Stepper>
              </Form>
            </FormProvider>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showCloseAlert} onOpenChange={() => { setShowCloseAlert(false); onOpenChange?.(false); } }>
        <AlertDialogContent className='max-md:rc-w-[96%] rc-rounded-lg !rc-z-[301]'>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to close?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. If you close now, all your progress will
              be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setShowCloseAlert(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onOpenChange?.(false);
                setShowCloseAlert(false);
              }}
            >
              Close anyway
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!error} onOpenChange={() => setError(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Upload Failed</AlertDialogTitle>
            <AlertDialogDescription>{error}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button onClick={() => setError(null)}>Close</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
